// NOTE: This document is a first-pass UK B2B SaaS Terms of Service drafted for the
// TS1 monitoring platform. It has not yet been reviewed by a solicitor. Schedule a
// one-off review with a UK SaaS/IP solicitor before relying on it in a dispute.

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const lastUpdated = '11 May 2026';

const sections = [
  {
    id: 'introduction',
    title: '1. Introduction and acceptance',
    body: (
      <>
        <p>
          These Terms of Service (the &quot;Terms&quot;) govern your access to and use of the TS1 monitoring
          platform, dashboard, mobile applications, APIs, and related services (collectively, the
          &quot;Service&quot;) provided by <strong>Thermal Systems Ltd</strong> (&quot;we&quot;,
          &quot;our&quot;, &quot;us&quot; or &quot;Thermal Systems&quot;), a company registered in England and
          Wales.
        </p>
        <p className="mt-3">
          By accessing or using the Service, by creating or being issued an account on the Service, or by
          connecting any base unit, sensor, or other hardware to the Service, you (the &quot;Customer&quot; or
          &quot;you&quot;) agree to be bound by these Terms. If you are accepting these Terms on behalf of an
          organisation, you confirm that you have authority to bind that organisation, and references to
          &quot;you&quot; include that organisation.
        </p>
        <p className="mt-3">
          If you do not agree to these Terms, do not access or use the Service.
        </p>
      </>
    ),
  },
  {
    id: 'service-description',
    title: '2. The Service',
    body: (
      <>
        <p>
          The Service is a cloud-hosted monitoring platform that ingests data from supported base units,
          sensors, and alarm relay units, displays that data through a web dashboard and mobile applications,
          and can issue notifications (including email and SMS) when readings exceed configured thresholds or
          when certain system conditions are detected.
        </p>
        <p className="mt-3">
          The Service is provided on a software-as-a-service basis. We may modify, add to, or remove features
          of the Service from time to time. We will not make material changes that materially reduce the
          functionality of the Service for paying customers during a paid term without reasonable notice.
        </p>
      </>
    ),
  },
  {
    id: 'accounts',
    title: '3. Accounts and access',
    body: (
      <ul className="list-disc pl-6 space-y-2">
        <li>
          Each user must have their own account and must not share login credentials. You are responsible for
          all activity that occurs under your accounts.
        </li>
        <li>
          You must keep credentials, biometric enrolments, and contact details (including alarm-recipient
          email addresses and mobile numbers) accurate and up to date at all times.
        </li>
        <li>
          You must notify us promptly of any suspected unauthorised access, lost device, or compromised
          credential.
        </li>
        <li>
          We may suspend or terminate accounts that we reasonably believe are being used in breach of these
          Terms, that pose a security risk, or where required by law.
        </li>
      </ul>
    ),
  },
  {
    id: 'customer-responsibilities',
    title: '4. Your responsibilities',
    body: (
      <>
        <p>
          You are solely responsible for the correct installation, configuration, and operational use of the
          Service in your environment, including (without limitation):
        </p>
        <ul className="list-disc pl-6 space-y-2 mt-3">
          <li>
            Correct physical placement, mounting, calibration, and routine verification of sensors, base
            units, and any other connected hardware.
          </li>
          <li>
            Correct configuration of alarm thresholds, delays, escalation rules, repeat intervals,
            zones, sites, user permissions, and notification recipients.
          </li>
          <li>
            Ensuring that the persons designated to receive alarm notifications are available, equipped to
            receive notifications, and trained to respond to them.
          </li>
          <li>
            Maintaining your own back-up monitoring procedures, manual checks, and contingency plans
            appropriate to the value, sensitivity, and regulatory status of the goods or processes being
            monitored.
          </li>
          <li>
            Complying with all laws and regulations applicable to your use of the Service, including those
            relating to food safety, pharmaceutical and vaccine storage (including MHRA, GxP and equivalent
            regimes), blood and tissue handling, laboratory specimens, HACCP, and any other regulatory
            framework that applies to your operations.
          </li>
          <li>
            Reviewing readings, alarms, and audit logs at appropriate intervals and acting on them.
          </li>
        </ul>
        <p className="mt-3">
          The Service is provided as a monitoring aid. It does not replace your own primary compliance,
          inspection, calibration, or operational obligations.
        </p>
      </>
    ),
  },
  {
    id: 'acceptable-use',
    title: '5. Acceptable use',
    body: (
      <>
        <p>You agree that you will not, and will not permit any user to:</p>
        <ul className="list-disc pl-6 space-y-2 mt-3">
          <li>
            Use the Service for any unlawful purpose, or to store, transmit, or process unlawful content.
          </li>
          <li>
            Reverse engineer, decompile, disassemble, or otherwise attempt to derive the source code of any
            part of the Service, except to the extent permitted by applicable mandatory law.
          </li>
          <li>
            Resell, sublicense, white-label, or otherwise commercially exploit the Service except under a
            separate written distributor or reseller agreement with us.
          </li>
          <li>
            Probe, scan, or test the vulnerability of the Service, or attempt to bypass any security or
            authentication mechanism, without our prior written consent.
          </li>
          <li>
            Use the Service to send unsolicited communications or in any way that infringes the privacy or
            other rights of any person.
          </li>
          <li>
            Interfere with or disrupt the integrity or performance of the Service, including by introducing
            malware or by submitting data designed to overload or destabilise the platform.
          </li>
          <li>
            Use the Service in any application where its failure could reasonably be expected to result in
            death, personal injury, or severe environmental damage (see section 8).
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'intellectual-property',
    title: '6. Intellectual property',
    body: (
      <>
        <p>
          The Service, including all software, designs, interfaces, text, graphics, logos, and documentation
          (other than your data), is and remains the property of Thermal Systems Ltd or its licensors. We
          grant you a non-exclusive, non-transferable, revocable licence to access and use the Service in
          accordance with these Terms during your subscription term.
        </p>
        <p className="mt-3">
          You retain all rights in the data you submit to or generate through the Service (&quot;Customer
          Data&quot;), and grant us a non-exclusive licence to process Customer Data solely to provide,
          maintain, secure, and improve the Service. We may use aggregated, anonymised, and de-identified data
          derived from Customer Data for any lawful purpose.
        </p>
        <p className="mt-3">
          You retain ownership of any branding, logos, colours, or images you provide for display within your
          tenant, and grant us a limited licence to display them within the Service for that purpose.
        </p>
      </>
    ),
  },
  {
    id: 'fees',
    title: '7. Fees and payment',
    body: (
      <p>
        Where fees apply, they will be set out in a separate written order, quote, subscription confirmation,
        or distributor agreement. Unless stated otherwise: fees are in pounds sterling, exclusive of VAT and
        any applicable taxes; invoices are payable within 30 days of invoice date; and we may suspend the
        Service if undisputed amounts remain unpaid for more than 30 days following written notice. Any free,
        trial, or evaluation use of the Service is provided at our discretion and may be modified or
        withdrawn at any time.
      </p>
    ),
  },
  {
    id: 'service-availability',
    title: '8. Service availability and "as is" basis',
    body: (
      <>
        <p>
          The Service is provided <strong>on an &quot;as is&quot; and &quot;as available&quot; basis</strong>.
          We do not warrant that the Service will be uninterrupted, error-free, free of harmful components, or
          that it will meet your specific requirements. Save as expressly stated in any separate signed
          written agreement, no service-level commitments are provided.
        </p>
        <p className="mt-3">
          The Service relies on third-party infrastructure (including but not limited to cloud hosting, edge
          networks, database services, SMS providers, email providers, mobile networks, and the public
          internet) and on hardware (base units, sensors, alarm relay units) which may be manufactured by
          parties other than us. Outages, delays, faults, or failures in any of these third-party components
          or in hardware are outside our reasonable control.
        </p>
        <p className="mt-3">
          <strong>
            The Service is not authorised for use as a critical component in any life-support, surgical,
            safety, or other system where its failure could reasonably be expected to cause death, personal
            injury, or serious environmental damage.
          </strong>{' '}
          You must not rely on the Service as a sole or primary control where such a failure could have those
          consequences.
        </p>
      </>
    ),
  },
  {
    id: 'disclaimer',
    title: '9. Disclaimers',
    body: (
      <>
        <p>
          To the maximum extent permitted by law, we disclaim all warranties, conditions, representations, and
          terms (whether express or implied by statute, common law, custom, or otherwise) in relation to the
          Service, including any implied warranties of satisfactory quality, fitness for a particular purpose,
          non-infringement, and accuracy.
        </p>
        <p className="mt-3">
          Without limiting the generality of the foregoing, we do not warrant that:
        </p>
        <ul className="list-disc pl-6 space-y-2 mt-3">
          <li>Readings displayed by the Service are accurate, complete, or current at any given moment.</li>
          <li>
            Alarms will trigger on time, in all cases, or be successfully delivered to any given recipient or
            device.
          </li>
          <li>
            SMS, email, push, or other notifications will be delivered without delay, in order, or at all,
            given that delivery depends on third-party carriers, operating systems, and networks outside our
            control.
          </li>
          <li>
            The hardware connected to the Service (sensors, base units, alarm relay units, calibration
            instruments) will operate correctly, remain calibrated, or be fit for any particular purpose.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'liability',
    title: '10. Limitation of liability',
    body: (
      <>
        <p className="font-semibold">
          Please read this section carefully. It limits the circumstances in which we are liable to you, and
          the amount we are liable for.
        </p>
        <p className="mt-3">
          <strong>Excluded losses.</strong> Subject only to the carve-outs in the final paragraph of this
          section, we will <strong>not</strong> be liable to you, whether in contract, tort (including
          negligence), breach of statutory duty, or otherwise, for any of the following, however arising:
        </p>
        <ul className="list-disc pl-6 space-y-2 mt-3">
          <li>
            <strong>
              Any loss, damage, spoilage, contamination, expiry, deterioration, destruction, recall, or
              write-off of stock, products, goods, food, beverages, pharmaceuticals, vaccines, blood,
              tissue, biological samples, laboratory specimens, perishable goods, or any other physical
              goods of any nature whatsoever, whether owned by you, your customers, or any third party,
              that are monitored by or in connection with the Service.
            </strong>
          </li>
          <li>
            Any loss, damage, or liability arising from an alarm that did not trigger, triggered late,
            triggered but was not received, was received but was missed, dismissed, ignored, or acted on
            incorrectly, or from any incorrect threshold, delay, escalation rule, recipient list, or other
            configuration.
          </li>
          <li>
            Any loss, damage, or liability arising from incorrect installation, placement, mounting, wiring,
            calibration, configuration, or operational use of sensors, base units, alarm relay units, or any
            other hardware, whether by you, by us, or by a third party.
          </li>
          <li>
            Any loss, damage, or liability arising from sensor failure, base unit failure, communications
            failure, hardware drift, battery exhaustion, signal loss, or any other hardware-related cause.
          </li>
          <li>
            Any loss, damage, or liability arising from outages, latency, delivery failure, or other faults
            in third-party services on which the Service depends, including cloud hosting providers, database
            providers, SMS gateways, email providers, mobile networks, and the public internet.
          </li>
          <li>
            Any loss of profits, loss of business, loss of revenue, loss of contracts, loss of opportunity,
            loss of goodwill, loss of reputation, loss of anticipated savings, business interruption, or
            wasted management or staff time.
          </li>
          <li>
            Any loss or corruption of data, except to the extent of our obligations under applicable data
            protection law in respect of personal data.
          </li>
          <li>
            Any indirect, special, consequential, incidental, exemplary, or punitive damages, regardless of
            whether we were advised of the possibility of such damages.
          </li>
        </ul>
        <p className="mt-4">
          <strong>Aggregate cap.</strong> Subject only to the carve-outs in the final paragraph of this
          section, our total aggregate liability to you arising out of or in connection with the Service
          and these Terms, whether in contract, tort (including negligence), breach of statutory duty, or
          otherwise, in any 12-month period, will not exceed the greater of (a) the total fees actually paid
          by you to us for the Service in the 12 months immediately preceding the event giving rise to the
          claim; or (b) £100 where no fees have been paid.
        </p>
        <p className="mt-4">
          <strong>Customer indemnity.</strong> You will indemnify us against all claims, losses, damages,
          fines, and reasonable costs (including legal fees) suffered or incurred by us in connection with
          any claim brought against us by a third party arising out of your use of the Service in breach of
          these Terms, your acts or omissions in operating the Service in your environment, or the goods or
          processes you choose to monitor with the Service.
        </p>
        <p className="mt-4">
          <strong>Carve-outs that cannot be excluded.</strong> Nothing in these Terms excludes or limits
          liability for: (i) death or personal injury caused by negligence; (ii) fraud or fraudulent
          misrepresentation; (iii) any other liability which cannot lawfully be excluded or limited under the
          laws of England and Wales. Where any exclusion or limitation in this section is held to be
          unenforceable, the remaining exclusions and limitations will continue to apply.
        </p>
      </>
    ),
  },
  {
    id: 'data-protection',
    title: '11. Data protection',
    body: (
      <p>
        Our handling of personal data is described in our{' '}
        <Link to="/privacy" className="text-indigo-600 dark:text-indigo-400 hover:underline">
          Privacy Policy
        </Link>{' '}
        and, where we process personal data on your behalf, our{' '}
        <Link to="/dpa" className="text-indigo-600 dark:text-indigo-400 hover:underline">
          Data Processing Agreement
        </Link>{' '}
        forms part of these Terms. Our use of cookies and similar technologies is described in our{' '}
        <Link to="/cookies" className="text-indigo-600 dark:text-indigo-400 hover:underline">
          Cookie Policy
        </Link>
        .
      </p>
    ),
  },
  {
    id: 'confidentiality',
    title: '12. Confidentiality',
    body: (
      <p>
        Each party will keep confidential any non-public information of the other party that is identified as
        confidential or that ought reasonably to be understood to be confidential, and will use it only for
        the purposes of these Terms. This obligation does not apply to information that is already public,
        is independently developed, is lawfully received from a third party, or is required to be disclosed
        by law or by a competent authority.
      </p>
    ),
  },
  {
    id: 'term-termination',
    title: '13. Term and termination',
    body: (
      <ul className="list-disc pl-6 space-y-2">
        <li>
          These Terms apply for as long as you use the Service or hold an account on it.
        </li>
        <li>
          We may suspend or terminate your access immediately if you materially breach these Terms (including
          the acceptable use restrictions), if your account is being used in a way that poses a security
          risk, if required by law, or if undisputed fees remain unpaid more than 30 days after written
          notice.
        </li>
        <li>
          You may stop using the Service at any time. Where you have a paid subscription, termination rights
          and refunds (if any) are governed by the relevant order or subscription confirmation.
        </li>
        <li>
          On termination we may delete or anonymise Customer Data after a reasonable retention period.
          Sections that by their nature should survive (including sections 6, 9, 10, 12, 15 and 16) will
          continue after termination.
        </li>
      </ul>
    ),
  },
  {
    id: 'force-majeure',
    title: '14. Force majeure',
    body: (
      <p>
        Neither party will be liable for any delay or failure to perform under these Terms (other than
        payment obligations) caused by events beyond its reasonable control, including acts of God, war,
        terrorism, civil unrest, pandemics, government action, strikes, failure of utilities, failure of the
        public internet, or failures of third-party services.
      </p>
    ),
  },
  {
    id: 'changes',
    title: '15. Changes to these Terms',
    body: (
      <p>
        We may update these Terms from time to time. The updated version will be posted on this page with a
        new &quot;Last updated&quot; date. Where changes are material we will use reasonable efforts to notify
        active customers in advance (for example, by email or in-product notice). Your continued use of the
        Service after the effective date constitutes acceptance of the updated Terms.
      </p>
    ),
  },
  {
    id: 'general',
    title: '16. General',
    body: (
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Governing law.</strong> These Terms are governed by the laws of England and Wales. The
          courts of England and Wales have exclusive jurisdiction over any dispute arising from or relating
          to these Terms or the Service, save that we may bring proceedings to protect our intellectual
          property in any jurisdiction.
        </li>
        <li>
          <strong>Assignment.</strong> You may not assign or transfer these Terms without our prior written
          consent. We may assign these Terms in connection with a merger, acquisition, reorganisation, or
          sale of substantially all of our assets.
        </li>
        <li>
          <strong>No waiver.</strong> A failure or delay by either party to enforce any provision of these
          Terms is not a waiver of that provision.
        </li>
        <li>
          <strong>Severability.</strong> If any provision of these Terms is held to be unenforceable, the
          remaining provisions will continue in full force and effect, and the unenforceable provision will
          be modified to the minimum extent necessary to make it enforceable.
        </li>
        <li>
          <strong>Third-party rights.</strong> A person who is not a party to these Terms has no rights
          under the Contracts (Rights of Third Parties) Act 1999 to enforce any term of them.
        </li>
        <li>
          <strong>Entire agreement.</strong> These Terms (together with the Privacy Policy, Cookie Policy,
          Data Processing Agreement, and any signed order or distributor agreement) constitute the entire
          agreement between you and us in relation to the Service.
        </li>
      </ul>
    ),
  },
  {
    id: 'contact',
    title: '17. Contact us',
    body: (
      <p>
        Questions about these Terms can be sent to Thermal Systems Ltd via our website{' '}
        <a
          href="https://thermal-systems.co.uk"
          target="_blank"
          rel="noopener noreferrer"
          className="text-indigo-600 dark:text-indigo-400 hover:underline"
        >
          thermal-systems.co.uk
        </a>
        , by telephone on{' '}
        <a href="tel:02034881030" className="text-indigo-600 dark:text-indigo-400 hover:underline">
          020 3488 1030
        </a>
        , or through the contact channels your organisation has been given for support.
      </p>
    ),
  },
];

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-app-canvas dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
        <Link
          to="/login"
          className="inline-flex items-center text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300 mb-8"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to sign in
        </Link>

        <header className="mb-10 pb-8 border-b border-gray-200 dark:border-gray-700">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">Terms of Service</h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Last updated: {lastUpdated}</p>
          <p className="mt-4 text-gray-600 dark:text-gray-300">
            These Terms govern your use of the TS1 monitoring platform operated by Thermal Systems Ltd.
            Please read them carefully, in particular the disclaimers in section 9 and the limitation of
            liability in section 10.
          </p>
        </header>

        <nav className="mb-10 p-4 rounded-lg bg-app-panel dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Contents</h2>
          <ul className="text-sm space-y-1 text-indigo-600 dark:text-indigo-400">
            {sections.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`} className="hover:underline">
                  {s.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="space-y-10 text-gray-700 dark:text-gray-300 text-sm sm:text-base leading-relaxed">
          {sections.map((s) => (
            <section key={s.id} id={s.id}>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">{s.title}</h2>
              {s.body}
            </section>
          ))}
        </div>

        <footer className="mt-14 pt-8 border-t border-gray-200 dark:border-gray-700 text-center text-sm text-gray-500 dark:text-gray-400">
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mb-3">
            <Link to="/compliance" className="hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline">
              Security &amp; Compliance
            </Link>
            <Link to="/privacy" className="hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline">
              Privacy Policy
            </Link>
            <Link to="/cookies" className="hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline">
              Cookie Policy
            </Link>
            <Link to="/dpa" className="hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline">
              Data Processing Agreement
            </Link>
          </div>
          <p>© {new Date().getFullYear()} Thermal Systems Ltd. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
}
